/* infinite-scroll.js: replaces click-through pagination on collection product grids with
   infinite scroll. An IntersectionObserver watches a sentinel placed just above the
   pagination nav; when it enters the viewport, the next page is fetched via the Section
   Rendering API (?section_id=...&page=N) against the same collection URL, the fetched
   #product-grid's <li> cards are appended to the live grid, and the URL is updated with
   history.replaceState (no navigation, no reload).

   The server-rendered pagination links (snippets/pagination.liquid) stay in the DOM as the
   no-JS / failure fallback: hidden on successful init, re-shown if a fetch ever fails so the
   visitor can keep browsing by click. Every product card the section renders already carries
   the `unless product.type == 'Extended Sizes (hidden)'` guard (see
   sections/main-collection-product-grid.liquid), so appended pages exclude the linked-products
   backing products exactly like the first page does; nothing here bypasses that guard.

   Fully defensive: this file wires up progressive enhancement only. Any failure (fetch error,
   malformed response, missing browser API) leaves the native paginated page working. */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window) || !('fetch' in window) || !window.DOMParser) return;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var containers = document.querySelectorAll('[data-infinite-scroll]');
    Array.prototype.forEach.call(containers, function (container) {
      try {
        initInfiniteScroll(container);
      } catch (e) {
        if (window.console) console.warn('[infinite-scroll]', e);
      }
    });
  });

  function initInfiniteScroll(container) {
    var grid = container.querySelector('#product-grid');
    // The empty-results state also uses id="product-grid" but on a <div>, not a <ul>; skip it.
    if (!grid || grid.tagName !== 'UL') return;

    var sectionId = container.getAttribute('data-section-id');
    var baseUrl = container.getAttribute('data-url');
    var currentPage = parseInt(container.getAttribute('data-current-page'), 10) || 1;
    var totalPages = parseInt(container.getAttribute('data-total-pages'), 10) || 1;
    if (!sectionId || !baseUrl || currentPage >= totalPages) return;

    var sentinel = container.querySelector('[data-infinite-scroll-sentinel]');
    var status = container.querySelector('[data-infinite-scroll-status]');
    var fallback = container.querySelector('[data-pagination-fallback]');
    if (!sentinel) return;

    var loading = false;
    var seen = {};
    Array.prototype.forEach.call(grid.children, function (li) {
      var id = li.getAttribute('data-product-id');
      if (id) seen[id] = true;
    });

    // JS is driving now: hide the click-through pagination nav (still there if we bail out).
    if (fallback) fallback.classList.add('hidden');

    function buildUrl(page, withSectionId) {
      var url = new URL(baseUrl, window.location.origin);
      var current = new URL(window.location.href);
      current.searchParams.forEach(function (value, key) {
        if (key !== 'page' && key !== 'section_id') url.searchParams.set(key, value);
      });
      if (page > 1) url.searchParams.set('page', page);
      if (withSectionId) url.searchParams.set('section_id', sectionId);
      return url;
    }

    function setLoading(isLoading) {
      loading = isLoading;
      if (status) status.classList.toggle('hidden', !isLoading);
    }

    function fail(err) {
      if (window.console) console.warn('[infinite-scroll]', err);
      observer.disconnect();
      setLoading(false);
      if (fallback) fallback.classList.remove('hidden');
    }

    function loadNext() {
      if (loading || currentPage >= totalPages) return;
      var nextPage = currentPage + 1;
      setLoading(true);
      fetch(buildUrl(nextPage, true).toString(), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('section fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.getElementById('product-grid');
          if (!newGrid || newGrid.tagName !== 'UL') throw new Error('no product grid in response');
          var items = Array.prototype.slice.call(newGrid.children);
          items.forEach(function (li) {
            var id = li.getAttribute('data-product-id');
            if (id && seen[id]) return; // dedupe safety net, should not trigger in practice
            if (id) seen[id] = true;
            // card-product.liquid emits per-card <link rel="stylesheet"> tags on the first
            // card of every fresh render; those styles are already loaded on the page, so
            // drop the duplicates rather than re-inserting them into the live DOM.
            var links = li.querySelectorAll('link[rel="stylesheet"]');
            Array.prototype.forEach.call(links, function (l) {
              if (l.parentNode) l.parentNode.removeChild(l);
            });
            grid.appendChild(li);
          });
          currentPage = nextPage;
          container.setAttribute('data-current-page', String(currentPage));
          var cleanUrl = buildUrl(currentPage, false);
          window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
          setLoading(false);
          if (currentPage >= totalPages) {
            observer.disconnect();
          } else {
            // The sentinel can still be sitting inside the (large) rootMargin zone after the
            // append, in which case its intersection state never actually changes and the
            // observer would otherwise stay silent until the visitor scrolls again. Re-observing
            // forces a fresh callback with the current state, so a tall viewport / fast scroll
            // keeps streaming pages in without waiting on a manual scroll transition.
            observer.unobserve(sentinel);
            observer.observe(sentinel);
          }
        })
        .catch(fail);
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNext();
        });
      },
      { rootMargin: '600px 0px 600px 0px' }
    );
    observer.observe(sentinel);
  }
})();
